import type * as Types from '@aeppic/document'

export type MatchConditions = {
  conditions: Condition[]
}

export type MatchExpressionMultiMatch = MatchConditions & { operator: ExplicitOperator }

export type QueryParameterKey = string|number

export type MatchExpression = MatchExpressionMultiMatch|MatchConditions

export type ReferenceRelationship = {
  type: 'reference'
  field: string
}

export type NamedRelationship = {
  type: 'named'
  name: string
}

export type QueryRelationship = ReferenceRelationship|NamedRelationship

export type RelationshipMatchNode = MatchConditions & {
  kind: 'relationship'
  relationship: QueryRelationship
  quantifier: 'some'
  query: Query
}

export type Condition = FieldMatchNode|RelationshipMatchNode|MatchExpression

export function isQuery(query: any): query is Query {
  if (query && typeof query === 'object' &&  'graph' in query && 'parameters' in query) {
    return true
  } else {
    return false
  }
}

export function isRelationshipMatchNode(condition: Condition): condition is RelationshipMatchNode {
  return Boolean(condition && 'kind' in condition && condition.kind === 'relationship')
}

export function queryContainsRelationships(query: Query): boolean {
  return conditionContainsRelationships(query.graph)
}

function conditionContainsRelationships(condition: Condition): boolean {
  if (isRelationshipMatchNode(condition)) {
    return true
  }

  if (isFieldMatchNode(condition)) {
    return false
  }

  return condition.conditions.some(conditionContainsRelationships)
}

export type Query = {
  extensions?: {
    relationships?: 1
  }
  graph: MatchExpression
  parameters: QueryParameters
  options?: MatchOptions
  findOptions?: QueryFindOptions
}

export type QueryBasicFieldSortInfo = {
  descending?: boolean
  missingToEnd?: boolean
}

export type QuerySingleFieldSortInfo = QueryBasicFieldSortInfo & {
  field: string
}

export type QueryMultiFallThroughFieldSortInfo = QueryBasicFieldSortInfo & {
  fields: string[]
}

export type QueryFallThroughFieldSortInfo = QueryBasicFieldSortInfo & {
  fallthrough: string[]
}

export type QueryFieldSortInfo = QuerySingleFieldSortInfo | QueryMultiFallThroughFieldSortInfo | QueryFallThroughFieldSortInfo
export type QuerySortOption = string | QueryFieldSortInfo | (string | QueryFieldSortInfo)[]

export type QueryFindOptions = {
  sort?: QuerySortOption
  size?: number
}

// import { Form } from '../form'
// export type FormLookupFunction = (id: string, v: string) => Form|Promise<Form>
// export type DocumentLookupFunction = (id: string, v?: string) => Types.Document|Promise<Types.Document>

export type MatchOptions = {
  /*
   * Current datetime in full ISO format
   * 
   * e.g: 2017-12-07T00:00:00+01:00 OR 2032-02-01T03:02:01Z
   */
  nowUtcTz?: string
  searchAllFields?: boolean
}

export interface MatchQueryFunction {
  (document: Types.Document): boolean
  displayName?: string
}

export interface GenericMatchQueryFunction {
  (document: Types.Document, parameters: QueryParameters, options: MatchOptions): boolean
  matchFunctionId: Symbol
  displayName?: string
}

export type QueryParameterScalar = string|number|boolean
export type QueryParameterValue = QueryParameterScalar|QueryParameterScalar[]
export interface StringQueryParameters {
  [key: string]: QueryParameterValue // number|DateTime|Duration
}

export type QueryParameters = StringQueryParameters | Array<QueryParameterValue>

export type QueryParameterReference = { id: string|number }

export type RawFieldMatchNode = {
  field: string
  fieldPrefix?: string
  term?: string
  term_max?: string
  term_min?: string
  termQuotes?: string
  similarity?: number
  boost?: number
  termPrefix?: string
  inclusive?: boolean
  /**
   * A range on a date field in `data` whose bounds carry an offset or `Z`
   * (taken from another document's field): Aeppic 4 cuts the offset off and
   * compares the wall-clock times as written, without ordering by instant.
   * Without it, Aeppic 4 refuses such a bound. Aeppic 3 ignores it.
   */
  comparedAsWritten?: boolean
}

export type FieldMatchNode = Pick<RawFieldMatchNode, Exclude<keyof RawFieldMatchNode, 'term' | 'term_min'|'term_max'>> & {
  term?: QueryParameterReference
  term_max?: QueryParameterReference
  term_min?: QueryParameterReference
}

export type DefaultOperator = 'AND'|'OR'
export type ExplicitOperator = 'AND'|'OR'
export type RawParsedOperator = ExplicitOperator|'NOT'

export type IntermediateNode = {
  left: QueryGraphNode
  operator?: RawParsedOperator|'<implicit>'
  right?: QueryGraphNode
}

export type QueryGraphNode = IntermediateNode | RawFieldMatchNode

export function isFieldMatchNode(object: any): object is FieldMatchNode {
  return 'field' in object
}

export function isIntermediaryNode(node: QueryGraphNode): node is IntermediateNode {
  return 'left' in node || 'operator' in node
}

export function isLeafNode(node: QueryGraphNode): node is RawFieldMatchNode {
  return !isIntermediaryNode(node)
}
